import { Fragment, type JSX, useState } from 'react'
import { useParams } from 'react-router'

import { useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'

import { InlineMarkdown } from '../components/InlineMarkdown.js'
import { MobileGutter } from '../components/MobileGutter.js'
import { PageMeta } from '../components/PageMeta.js'
import { PostDate } from '../components/PostDate.js'
import { GutterBlock } from '../components/PostGutter.js'
import { ProvenanceToggle } from '../components/ProvenanceToggle.js'
import { TagList } from '../components/TagList.js'
import { findPost, postMarkdownPath } from '../content.js'
import type { PostSection } from '../content-types.js'
import { withBase } from '../site.js'
import { NotFound } from './NotFound.js'

/**
 * One post at `/<slug>/` — the whole point of the site.
 *
 * The title block is centred, with the date, the tags and BOTH toggles centred
 * directly beneath it: the toggles on ONE row, summaries first then prompts,
 * both ON by default, and the row never wraps (the phone's header row is not
 * rendered at all — the bar carries the same two switches there).
 *
 * The share line is REAL text in this post's HTML — "41% of the words were
 * written by an AI, from 12 prompts" — never a placeholder a script fills in,
 * so a crawler and a reader without JavaScript both see it.
 *
 * The body is laid out from the section model: every paragraph carries its
 * provenance span, an `ai` span is highlighted, and each prompt appears in
 * exactly ONE place — its block beside the FIRST paragraph it produced, never
 * once per paragraph and never duplicated in a popover. A post with nothing for
 * the gutter renders no grid at all: the prose takes the full reading measure,
 * centred, with no toggles and no phone bar.
 */
export function Post(): JSX.Element {
  const cm = getClassMap()
  const { t } = useTranslation()
  const { slug = '' } = useParams<{ slug: string }>()
  const post = findPost(slug)
  const [showSummaries, setShowSummaries] = useState(true)
  const [showPrompts, setShowPrompts] = useState(true)
  if (!post) return <NotFound />

  const hasSummary =
    post.sections.some((section) => Boolean(section.summary)) || Boolean(post.leadSummary)
  const hasPrompts = post.sections.some((section) =>
    section.paragraphs.some((p) => p.origin === 'ai' && p.prompt),
  )
  const hasGutter = hasSummary || hasPrompts

  const shareLine = t(
    post.promptCount === 1 ? 'post.shareLineOne' : 'post.shareLine',
    { percent: post.aiPercent, prompts: post.promptCount },
    {
      defaultValue:
        post.promptCount === 1
          ? '{{percent}}% of the words were written by an AI, from a single prompt'
          : '{{percent}}% of the words were written by an AI, from {{prompts}} prompts',
    },
  )

  /** The sections in reading order, the lead included. */
  const sections: PostSection[] = post.sections
    .filter((section) => section.paragraphs.length > 0 || section.heading)
    // The lead's TL;DR travels on its own field (it has no source heading), so
    // it is folded back in here where the page renders it.
    .map((section) =>
      section.id === 's-lead' && post.leadSummary
        ? { ...section, summary: post.leadSummary, summaryAvailable: post.leadSummaryAvailable }
        : section,
    )

  // Which prompt each section SHOWS: the first `ai` paragraph's prompt that no
  // earlier section has already shown. Computed once, here, so the page renders
  // any given prompt exactly once (§4) while the map keeps it per span (§3).
  const shownPrompts: Array<string | undefined> = []
  const already = new Set<string>()
  for (const section of sections) {
    const found = section.paragraphs.find(
      (p) => p.origin === 'ai' && p.prompt && !already.has(p.prompt),
    )
    shownPrompts.push(found?.prompt)
    if (found?.prompt) already.add(found.prompt)
  }

  const markdownLink = (
    <a className={cm.link} href={withBase(postMarkdownPath(post.slug))} data-mol-id="post-markdown">
      {t('post.markdown', undefined, { defaultValue: 'View this post as markdown' })}
    </a>
  )

  return (
    <article className={cm.cn('mol-post', cm.sp('py', 8))} data-mol-id="post-page">
      <PageMeta title={post.title} description={post.description} />

      <div className="mol-post-measure">
        <header className="mol-post-header" data-mol-id="post-header">
          <h1 className={cm.cn(cm.textSize('3xl'), cm.fontWeight('bold'))} data-mol-id="post-title">
            {post.title}
          </h1>
          <p className="mol-post-meta">
            <PostDate date={post.date} />
            {' · '}
            <span data-mol-id="post-reading-time">
              {t(
                'post.readingTime',
                { minutes: post.readingMinutes },
                { defaultValue: '{{minutes}} min read' },
              )}
            </span>
          </p>
          <div className="mol-post-tags" data-mol-id="post-tags">
            <TagList tags={post.tags} center />
          </div>
          <p className="mol-post-share" data-mol-id="post-share">
            {shareLine}
          </p>
          {hasGutter && (
            <div className="mol-post-toggles" data-mol-id="post-toggles">
              <ProvenanceToggle
                id="summary-toggle"
                label={t('post.summaries', undefined, { defaultValue: 'Summaries' })}
                checked={showSummaries}
                onChange={setShowSummaries}
              />
              <ProvenanceToggle
                id="prompt-toggle"
                label={t('post.prompts', undefined, { defaultValue: 'Prompts' })}
                checked={showPrompts}
                onChange={setShowPrompts}
              />
            </div>
          )}
        </header>
      </div>

      {hasGutter ? (
        <div className="mol-grid" data-mol-id="post-gutter-grid">
          {sections.map((section, i) => (
            <SectionRow
              key={section.id}
              section={section}
              shownPrompt={shownPrompts[i]}
              showSummaries={showSummaries}
              showPrompts={showPrompts}
            />
          ))}
        </div>
      ) : (
        // No gutter: the SAME `.mol-shell` every other page uses, so the prose
        // takes the full reading measure at one shared left edge (§5).
        <div className="mol-shell mol-shell-post">
          <div className="mol-prose-plain">
            {sections.map((section) => (
              <Fragment key={section.id}>
                <SectionHeading section={section} />
                {section.paragraphs.map((paragraph, i) => (
                  <div
                    key={i}
                    className="mol-paragraph"
                    dangerouslySetInnerHTML={{ __html: paragraph.html }}
                  />
                ))}
              </Fragment>
            ))}
          </div>
          <footer className="mol-post-footer" data-mol-id="post-footer">
            {markdownLink}
          </footer>
        </div>
      )}

      {hasGutter && (
        <div className="mol-post-measure">
          <footer className="mol-post-footer" data-mol-id="post-footer">
            {markdownLink}
            {post.transcripts.length > 0 && (
              <p className="mol-post-transcripts" data-mol-id="post-transcripts">
                <span className="mol-gutter-label">
                  {t('post.howWritten', undefined, { defaultValue: 'How this was written' })}
                </span>
                {post.transcripts.map((transcript) => (
                  <a key={transcript.file} className={cm.link} href={withBase(transcript.path)}>
                    {transcript.file}
                  </a>
                ))}
              </p>
            )}
          </footer>
        </div>
      )}

      {hasGutter && (
        <MobileGutter
          sections={post.sections}
          showSummary={showSummaries}
          showPrompts={showPrompts}
          onToggleSummary={setShowSummaries}
          onTogglePrompts={setShowPrompts}
        />
      )}
    </article>
  )
}

/** One section's heading, with the anchor the phone observers watch. */
function SectionHeading({ section }: { section: PostSection }): JSX.Element | null {
  const cm = getClassMap()
  if (!section.heading) return null
  const Tag = `h${Math.min(Math.max(section.level, 2), 6)}` as 'h2'
  return (
    <Tag
      id={section.id}
      className={cm.cn(cm.textSize('2xl'), cm.fontWeight('bold'))}
      data-mol-id={`section-${section.id}`}
    >
      {/* A heading carries INLINE MARKDOWN — `usePromise`, *emphasis*, a
          [link](url) — so it is rendered as inline markdown rather than printed
          raw, or the backticks reach the page as literal text instead of a
          code element. */}
      <InlineMarkdown text={section.heading} />
    </Tag>
  )
}

/**
 * One grid ROW per section: the paragraphs in the left column and the section's
 * commentary in the right. The row's two cells share one top edge, so the
 * block's top lines up with the section's heading (or, when it has none, its
 * first paragraph) — and the block is sticky inside its own grid area, so it
 * follows the reader while the section scrolls past and releases when the
 * section ends.
 */
function SectionRow({
  section,
  shownPrompt,
  showSummaries,
  showPrompts,
}: {
  section: PostSection
  /** The prompt this section shows, decided once by the parent. */
  shownPrompt?: string | undefined
  showSummaries: boolean
  showPrompts: boolean
}): JSX.Element {
  // The prompt is rendered ONCE, against the FIRST `ai` paragraph that carries
  // it — never once per paragraph, and never against a section whose opening
  // paragraph is the human's own.
  const promptParagraph = shownPrompt
    ? section.paragraphs.find((p) => p.origin === 'ai' && p.prompt === shownPrompt)
    : undefined
  return (
    <div className="mol-row" data-mol-id={`row-${section.id}`}>
      <div className="mol-pane">
        <SectionHeading section={section} />
        {section.paragraphs.map((paragraph, i) => (
          <div key={i} className="mol-cell">
            <div
              className={
                paragraph.origin === 'ai' ? 'mol-paragraph mol-ai' : 'mol-paragraph mol-human'
              }
              data-mol-origin={paragraph.origin}
              data-mol-id={`paragraph-${section.id}-${i}`}
              dangerouslySetInnerHTML={{ __html: paragraph.html }}
            />
          </div>
        ))}
      </div>
      <aside className="mol-gutter">
        <div className="mol-gutter-sticky">
          <GutterBlock
            id={section.id}
            summary={section.summary}
            paragraph={promptParagraph}
            showSummary={showSummaries}
            showPrompts={showPrompts}
          />
        </div>
      </aside>
    </div>
  )
}
